import * as Sentry from "@sentry/nextjs";
import { ORG_CHART_NODE_TEXT_STYLES, OrgChartNodeTextStyles, MAX_TEXT_LINES } from "./utils";
import { Sizes } from "frontend/data/shapes/sizes";

interface FontStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: string | number;
  lineHeight: number;
  letterSpacing?: number;
}

interface TextMeasurements {
  width: number;
  height: number;
}

function getFontStyleFromId(textStyleId: OrgChartNodeTextStyles): FontStyle {
  const style = ORG_CHART_NODE_TEXT_STYLES[textStyleId];
  return {
    fontFamily: style.fontFamily || "Poppins",
    fontSize: Sizes.get().orgChart.fontSize[textStyleId],
    fontWeight: (style.fontStyle as unknown as string) || "400", // fontStyle in Konva is actually fontWeight
    lineHeight: style.lineHeight || 1.2,
    letterSpacing: (style as unknown as { letterSpacing?: number }).letterSpacing,
  };
}

class CanvasTextMeasurer {
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;

  private getContext(): CanvasRenderingContext2D | null {
    if (!this.canvas) {
      this.canvas = document.createElement("canvas");
      this.canvas.width = 1;
      this.canvas.height = 1;
      this.context = this.canvas.getContext("2d");
      if (!this.context) {
        Sentry.captureMessage("Failed to get 2D context from canvas - using fallback measurements", {
          level: "warning",
          tags: { component: "org-chart", operation: "text-measurement" },
        });
        return null;
      }
    }

    if (!this.context) {
      Sentry.captureMessage("Canvas context is not available - using fallback measurements", {
        level: "warning",
        tags: { component: "org-chart", operation: "text-measurement" },
      });
      return null;
    }
    return this.context;
  }

  private applyFontToCanvas(context: CanvasRenderingContext2D, fontStyle: FontStyle): void {
    context.font = `${fontStyle.fontWeight} ${fontStyle.fontSize}px ${fontStyle.fontFamily}`;
  }

  private measureWidth(text: string, fontStyle: FontStyle): number {
    const context = this.getContext();
    if (!context) {
      // Fallback: estimate width based on character count and font size
      const avgCharWidth = fontStyle.fontSize * 0.6; // Rough approximation
      return text.length * avgCharWidth;
    }
    this.applyFontToCanvas(context, fontStyle);
    const metrics = context.measureText(text);
    return metrics.width;
  }

  private measureHeight(text: string, fontStyle: FontStyle, maxWidth: number): number {
    const context = this.getContext();
    const lineHeightPx = fontStyle.fontSize * fontStyle.lineHeight;

    if (!context) {
      // Fallback: estimate height based on text length and max width
      const avgCharWidth = fontStyle.fontSize * 0.6;
      const estimatedWidth = text.length * avgCharWidth;
      const estimatedLines = Math.ceil(estimatedWidth / maxWidth);
      const clampedLines = Math.min(estimatedLines, MAX_TEXT_LINES);
      return clampedLines * lineHeightPx;
    }

    this.applyFontToCanvas(context, fontStyle);

    // For single line text or if width doesn't exceed max, use font height
    const textWidth = context.measureText(text).width;
    if (!text.includes("\n") && textWidth <= maxWidth) {
      // Use advanced metrics if available, otherwise fallback to lineHeight
      const metrics = context.measureText(text);
      if ("fontBoundingBoxAscent" in metrics && "fontBoundingBoxDescent" in metrics) {
        const advancedMetrics = metrics as unknown as {
          fontBoundingBoxAscent: number;
          fontBoundingBoxDescent: number;
        };
        const fontHeight = advancedMetrics.fontBoundingBoxAscent + advancedMetrics.fontBoundingBoxDescent;
        return Math.max(fontHeight, lineHeightPx);
      }
      return lineHeightPx;
    }

    // For multi-line text, estimate number of lines using word wrapping
    const lines = this.estimateLineCount(text, maxWidth, context);
    const clampedLines = Math.min(lines, MAX_TEXT_LINES);
    return clampedLines * lineHeightPx;
  }

  private estimateLineCount(text: string, maxWidth: number, context: CanvasRenderingContext2D): number {
    const paragraphs = text.split("\n");
    let totalLines = 0;

    for (const paragraph of paragraphs) {
      if (paragraph.trim() === "") {
        totalLines += 1;
        continue;
      }

      const words = paragraph.trim().split(/\s+/);
      let currentLineWidth = 0;
      let linesInParagraph = 1;

      for (const word of words) {
        const wordWidth = context.measureText(word).width;
        const spaceWidth = currentLineWidth > 0 ? context.measureText(" ").width : 0;

        // Check if adding this word would exceed the line width
        if (currentLineWidth > 0 && currentLineWidth + spaceWidth + wordWidth > maxWidth) {
          linesInParagraph++;
          currentLineWidth = wordWidth;
        } else {
          currentLineWidth += spaceWidth + wordWidth;
        }
      }

      totalLines += linesInParagraph;
    }

    return totalLines;
  }

  measureWithStyleId(styleId: OrgChartNodeTextStyles, text: string, maxWidth: number): TextMeasurements {
    const normalizedText = text.trim();
    if (!normalizedText) {
      const fontStyle = getFontStyleFromId(styleId);
      return {
        width: 0,
        height: fontStyle.fontSize * fontStyle.lineHeight,
      };
    }

    const fontStyle = getFontStyleFromId(styleId);
    const width = this.measureWidth(normalizedText, fontStyle);
    const height = this.measureHeight(normalizedText, fontStyle, maxWidth);

    return { width, height };
  }
}

const canvasTextMeasurer = new CanvasTextMeasurer();

export function measureText(
  textStyleId: OrgChartNodeTextStyles,
  text: string,
  maxWidth: number //: number = ORG_CHART_NODE_WIDTH - 8 // Node width minus padding
): TextMeasurements {
  return canvasTextMeasurer.measureWithStyleId(textStyleId, text, maxWidth);
}

/**
 * Check if text overflows the maximum lines
 */
export function doesTextOverflow(
  text: string,
  width: number,
  textStyleId: OrgChartNodeTextStyles,
  fieldTitle?: string
): boolean {
  const displayText = (text || fieldTitle || "").trim();
  if (!displayText) return false;

  // IMPORTANT: We must not use the clamped height for overflow detection,
  // because measureHeight caps to MAX_TEXT_LINES. Compute the raw wrapped
  // line count using the same word-wrapping logic instead.
  const fontStyle = getFontStyleFromId(textStyleId);

  // Create a tiny canvas context for measurement
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    // Fallback: estimate based on text length
    const avgCharWidth = fontStyle.fontSize * 0.6;
    const estimatedWidth = displayText.length * avgCharWidth;
    const estimatedLines = Math.ceil(estimatedWidth / (width * 0.95));
    return estimatedLines > MAX_TEXT_LINES;
  }
  context.font = `${fontStyle.fontWeight} ${fontStyle.fontSize}px ${fontStyle.fontFamily}`;

  const maxWidth = width * 0.95;

  const paragraphs = displayText.split("\n");
  let totalLines = 0;
  for (const paragraph of paragraphs) {
    if (paragraph.trim() === "") {
      totalLines += 1;
      continue;
    }
    const words = paragraph.trim().split(/\s+/);
    let currentLineWidth = 0;
    let linesInParagraph = 1;
    for (const word of words) {
      const wordWidth = context.measureText(word).width;
      const spaceWidth = currentLineWidth > 0 ? context.measureText(" ").width : 0;
      if (currentLineWidth > 0 && currentLineWidth + spaceWidth + wordWidth > maxWidth) {
        linesInParagraph++;
        currentLineWidth = wordWidth;
      } else {
        currentLineWidth += spaceWidth + wordWidth;
      }
    }
    totalLines += linesInParagraph;
  }

  return totalLines > MAX_TEXT_LINES;
}
